import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import { apiClient } from '../api/client'
import type { CapexPlan, Worksite, WorkPackage, TheoreticalPlan } from '../components/capex/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractItem<T>(result: { data?: unknown }): T | undefined {
  if (!result.data) return undefined
  const d = result.data as { data?: T }
  if (d && typeof d === 'object' && 'data' in d) {
    return d.data
  }
  return result.data as T
}

function extractList<T>(result: { data?: unknown }): T[] {
  if (!result.data) return []
  const d = result.data as { data?: T[] }
  if (d && typeof d === 'object' && 'data' in d && Array.isArray(d.data)) {
    return d.data
  }
  if (Array.isArray(result.data)) return result.data as T[]
  return []
}

/**
 * Invalidate all capex-plan related queries.
 * Uses the predicate-based pattern required by openapi-react-query.
 */
function invalidateCapexQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as unknown[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        const path = queryKey[1]
        return typeof path === 'string' && path.includes('capex-plans')
      }
      return false
    },
  })
}

// ---------------------------------------------------------------------------
// Query Hooks
// ---------------------------------------------------------------------------

/**
 * List all capex plans across the organization.
 */
export function useOrgCapexPlans(
  org: string,
  options?: { workflowStatus?: string; archived?: boolean; enabled?: boolean }
) {
  const result = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/capex-plans',
    {
      params: {
        path: { orgCode: org },
        query: {
          workflow_status: options?.workflowStatus as 'draft' | 'submitted' | 'approved' | 'rejected' | undefined,
          archived: options?.archived,
        },
      },
    },
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      enabled: options?.enabled !== false && !!org,
    }
  )

  return {
    ...result,
    data: extractList<CapexPlan>(result),
  }
}

/**
 * List all capex plans for a given asset.
 * By default returns only active plans; pass archived=true to include archived ones.
 */
export function useCapexPlans(
  org: string,
  assetUid: string,
  options?: { archived?: boolean; enabled?: boolean }
) {
  const result = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}/capex-plans',
    {
      params: {
        path: { orgCode: org, assetUid },
        query: options?.archived !== undefined ? { archived: options.archived } : undefined,
      },
    },
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      enabled: options?.enabled !== false && !!org && !!assetUid,
    }
  )

  return {
    ...result,
    data: extractList<CapexPlan>(result),
  }
}

/**
 * Fetch a single capex plan with optional nested data.
 *
 * @param options.include  Comma-separated includes: "worksites" or "worksites.work_packages"
 */
export function useCapexPlan(
  org: string,
  planUid: string,
  options?: { include?: string; enabled?: boolean }
) {
  const result = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}',
    {
      params: {
        path: { orgCode: org, planUid },
        query: options?.include ? { include: options.include } : undefined,
      },
    },
    {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      enabled: options?.enabled !== false && !!org && !!planUid,
    }
  )

  return {
    ...result,
    data: extractItem<CapexPlan>(result),
  }
}

// ---------------------------------------------------------------------------
// Plan Mutations
// ---------------------------------------------------------------------------

/**
 * Create a new capex plan for an asset.
 */
export function useCreateCapexPlan(org: string, assetUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'post',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}/capex-plans',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      body: { name: string; horizon_start: number; horizon_end: number },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, assetUid } }, body },
        options
      )
    },
    mutateAsync: (
      body: { name: string; horizon_start: number; horizon_end: number },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, assetUid } }, body },
        options
      )
    },
  }
}

/**
 * Update capex plan metadata.
 * Pass ifMatch (the plan's updated_at ISO timestamp) for optimistic locking.
 */
export function useUpdateCapexPlan(org: string, planUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      {
        body,
        ifMatch,
      }: {
        body: { name?: string; horizon_start?: number; horizon_end?: number }
        ifMatch?: string
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        {
          params: {
            path: { orgCode: org, planUid },
            header: ifMatch ? { 'If-Match': ifMatch } : undefined,
          },
          body,
        },
        options
      )
    },
    mutateAsync: (
      {
        body,
        ifMatch,
      }: {
        body: { name?: string; horizon_start?: number; horizon_end?: number }
        ifMatch?: string
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        {
          params: {
            path: { orgCode: org, planUid },
            header: ifMatch ? { 'If-Match': ifMatch } : undefined,
          },
          body,
        },
        options
      )
    },
  }
}

/**
 * Submit a capex plan for owner approval.
 * Precondition: plan must be in 'draft' status.
 */
export function useSubmitCapexPlan(org: string, planUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}/submit',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      body: { note?: string } = {},
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, planUid } }, body },
        options
      )
    },
    mutateAsync: (
      body: { note?: string } = {},
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, planUid } }, body },
        options
      )
    },
  }
}

/**
 * Approve a submitted capex plan.
 * Precondition: plan must be in 'submitted' status.
 */
export function useApproveCapexPlan(org: string, planUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}/approve',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (options?: Parameters<typeof baseMutation.mutate>[1]) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, planUid } } },
        options
      )
    },
    mutateAsync: (options?: Parameters<typeof baseMutation.mutateAsync>[1]) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, planUid } } },
        options
      )
    },
  }
}

/**
 * Reject a submitted capex plan.
 * Reverts plan to 'draft' with version incremented.
 * Precondition: plan must be in 'submitted' status.
 */
export function useRejectCapexPlan(org: string, planUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}/reject',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      body: {
        category: 'budget' | 'priority' | 'timing' | 'missing' | 'scope' | 'other'
        note: string
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, planUid } }, body },
        options
      )
    },
    mutateAsync: (
      body: {
        category: 'budget' | 'priority' | 'timing' | 'missing' | 'scope' | 'other'
        note: string
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, planUid } }, body },
        options
      )
    },
  }
}

// ---------------------------------------------------------------------------
// Worksite Mutations
// ---------------------------------------------------------------------------

/**
 * Create a worksite within a capex plan.
 * Precondition: plan must be in 'draft' status.
 */
export function useCreateWorksite(org: string, planUid: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'post',
    '/api/v1/organizations/{orgCode}/capex-plans/{planUid}/worksites',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      body: {
        asset_uid?: string
        trade: 'structural' | 'envelope' | 'hvac' | 'electrical' | 'fire_safety' | 'vertical_transport' | 'plumbing'
        name: string
        sub?: string
        condition: number
        year: number
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, planUid } }, body: { ...body, sub: body.sub ?? '' } },
        options
      )
    },
    mutateAsync: (
      body: {
        asset_uid?: string
        trade: 'structural' | 'envelope' | 'hvac' | 'electrical' | 'fire_safety' | 'vertical_transport' | 'plumbing'
        name: string
        sub?: string
        condition: number
        year: number
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, planUid } }, body: { ...body, sub: body.sub ?? '' } },
        options
      )
    },
  }
}

/**
 * Fetch the always-live, read-only capex trajectory for an asset. This is a
 * pure projection computed from equipment state — nothing is persisted, so it
 * is refetched rather than cached long. Uses the raw client (cast to any) since
 * the endpoint is not yet in the generated schema.
 */
export function useTheoreticalPlan(org: string, assetUid: string) {
  return useQuery({
    queryKey: ['capex-theoretical', org, assetUid],
    enabled: !!org && !!assetUid,
    staleTime: 0,
    queryFn: async (): Promise<TheoreticalPlan> => {
      const { data, error } = await (apiClient as any).GET(
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/capex-theoretical',
        { params: { path: { orgCode: org, assetUid } } },
      )
      if (error) throw error
      return (data?.data ?? { worksites: [], summary: null }) as TheoreticalPlan
    },
  })
}

/**
 * Generate worksites for a draft plan by deriving them from the condition and
 * age of the equipment under the plan's asset. Idempotent — equipment already
 * carrying a worksite is skipped, so it is safe to re-run.
 *
 * Uses the raw openapi-fetch client (cast to any) because this endpoint is not
 * yet in the generated schema; it still goes through the auth middleware.
 */
export function useGenerateWorksites(org: string, planUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await (apiClient as any).POST(
        '/api/v1/organizations/{orgCode}/capex-plans/{planUid}/generate',
        { params: { path: { orgCode: org, planUid } } },
      )
      if (error) throw error
      return (data?.data ?? []) as Worksite[]
    },
    onSuccess: () => {
      invalidateCapexQueries(queryClient)
    },
  })
}

/**
 * Update a worksite.
 * Pass ifMatch (the worksite's updated_at ISO timestamp) for optimistic locking.
 * Precondition: parent plan must be in 'draft' status.
 */
export function useUpdateWorksite(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/worksites/{worksiteUid}',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      {
        worksiteUid,
        body,
        ifMatch,
      }: {
        worksiteUid: string
        body: Partial<Pick<Worksite, 'name' | 'sub' | 'year' | 'trade' | 'condition'>> & { status?: 'draft' | 'reviewed' | 'validated' | 'deferred'; defer_reason?: string; asset_uid?: string }
        ifMatch?: string
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        {
          params: {
            path: { orgCode: org, worksiteUid },
            header: ifMatch ? { 'If-Match': ifMatch } : undefined,
          },
          body,
        },
        options
      )
    },
    mutateAsync: (
      {
        worksiteUid,
        body,
        ifMatch,
      }: {
        worksiteUid: string
        body: Partial<Pick<Worksite, 'name' | 'sub' | 'year' | 'trade' | 'condition'>> & { status?: 'draft' | 'reviewed' | 'validated' | 'deferred'; defer_reason?: string; asset_uid?: string }
        ifMatch?: string
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        {
          params: {
            path: { orgCode: org, worksiteUid },
            header: ifMatch ? { 'If-Match': ifMatch } : undefined,
          },
          body,
        },
        options
      )
    },
  }
}

/**
 * Soft-delete a worksite (sets status to 'ignored').
 * Precondition: parent plan must be in 'draft' status.
 */
export function useDeleteWorksite(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'delete',
    '/api/v1/organizations/{orgCode}/worksites/{worksiteUid}',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      worksiteUid: string,
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, worksiteUid } } },
        options
      )
    },
    mutateAsync: (
      worksiteUid: string,
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, worksiteUid } } },
        options
      )
    },
  }
}

// ---------------------------------------------------------------------------
// Work Package Mutations
// ---------------------------------------------------------------------------

/**
 * Create a work package within a worksite.
 * Precondition: parent plan must be in 'draft' status.
 */
export function useCreateWorkPackage(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'post',
    '/api/v1/organizations/{orgCode}/worksites/{worksiteUid}/work-packages',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      {
        worksiteUid,
        body,
      }: {
        worksiteUid: string
        body: {
          label: string
          cost_low: number
          cost_high: number
          compliance_evaluation_uid?: string
        }
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, worksiteUid } }, body },
        options
      )
    },
    mutateAsync: (
      {
        worksiteUid,
        body,
      }: {
        worksiteUid: string
        body: {
          label: string
          cost_low: number
          cost_high: number
          compliance_evaluation_uid?: string
        }
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, worksiteUid } }, body },
        options
      )
    },
  }
}

/**
 * Update a work package.
 */
export function useUpdateWorkPackage(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/work-packages/{packageUid}',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      {
        packageUid,
        body,
      }: {
        packageUid: string
        body: Partial<Pick<WorkPackage, 'label' | 'cost_low' | 'cost_high' | 'quote_amount'>> & { compliance_evaluation_uid?: string }
      },
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, packageUid } }, body },
        options
      )
    },
    mutateAsync: (
      {
        packageUid,
        body,
      }: {
        packageUid: string
        body: Partial<Pick<WorkPackage, 'label' | 'cost_low' | 'cost_high' | 'quote_amount'>> & { compliance_evaluation_uid?: string }
      },
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, packageUid } }, body },
        options
      )
    },
  }
}

/**
 * Delete a quote from a work package.
 * Nulls quote_amount and archives any linked document.
 * Precondition: parent plan must be in 'draft' status.
 */
export function useDeleteQuote(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'delete',
    '/api/v1/organizations/{orgCode}/work-packages/{packageUid}/quote',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      packageUid: string,
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, packageUid } } },
        options
      )
    },
    mutateAsync: (
      packageUid: string,
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, packageUid } } },
        options
      )
    },
  }
}

/**
 * Hard-delete a work package.
 */
export function useDeleteWorkPackage(org: string) {
  const queryClient = useQueryClient()

  const baseMutation = $api.useMutation(
    'delete',
    '/api/v1/organizations/{orgCode}/work-packages/{packageUid}',
    {
      onSuccess: () => {
        invalidateCapexQueries(queryClient)
      },
    }
  )

  return {
    ...baseMutation,
    mutate: (
      packageUid: string,
      options?: Parameters<typeof baseMutation.mutate>[1]
    ) => {
      baseMutation.mutate(
        { params: { path: { orgCode: org, packageUid } } },
        options
      )
    },
    mutateAsync: (
      packageUid: string,
      options?: Parameters<typeof baseMutation.mutateAsync>[1]
    ) => {
      return baseMutation.mutateAsync(
        { params: { path: { orgCode: org, packageUid } } },
        options
      )
    },
  }
}
